import type { EventTemplateBindingItem, EventTemplateBindingSlot } from '@/api/templates';

export type BindingCoverageKind = 'ok' | 'partial' | 'gap' | 'legacy';

export type BindingCoverage = {
  kind: BindingCoverageKind;
  label: string;
  boundChannels: number;
  totalChannels: number;
  hasGeneric: boolean;
  hasLegacy: boolean;
  hasGap: boolean;
  isProblematic: boolean;
};

function slotIsLegacy(slot: EventTemplateBindingSlot): boolean {
  return (
    slot.runtime?.source_kind === 'legacy_fallback' ||
    Boolean(slot.warning?.toLowerCase().includes('legacy'))
  );
}

function slotHasDedicated(slot: EventTemplateBindingSlot): boolean {
  return Boolean(slot.has_binding && slot.template);
}

export function getEventBindingCoverage(binding: EventTemplateBindingItem): BindingCoverage {
  const channels = binding.channel_bindings || [];
  const generic = binding.generic_binding;
  const hasGeneric = slotHasDedicated(generic);
  const boundChannels = channels.filter(slotHasDedicated).length;
  const totalChannels = channels.length;
  const hasLegacy = [generic, ...channels].some(slotIsLegacy);
  const hasGap =
    !hasGeneric &&
    boundChannels === 0 &&
    channels.some((slot) => !slot.runtime?.has_template && !slot.template);

  let kind: BindingCoverageKind = 'ok';
  let label = `${boundChannels}/${totalChannels} каналов`;

  if (hasGap || (boundChannels === 0 && !hasGeneric && !hasLegacy)) {
    kind = 'gap';
    label = 'Нет привязки';
  } else if (hasLegacy) {
    kind = 'legacy';
    label = 'Legacy fallback';
  } else if (boundChannels < totalChannels || !hasGeneric) {
    kind = 'partial';
    label = hasGeneric
      ? `${boundChannels}/${totalChannels} overrides`
      : `Generic нет · ${boundChannels}/${totalChannels}`;
  } else {
    label = `Покрыто ${boundChannels}/${totalChannels}`;
  }

  return {
    kind,
    label,
    boundChannels,
    totalChannels,
    hasGeneric,
    hasLegacy,
    hasGap,
    isProblematic: kind === 'gap' || kind === 'legacy' || (kind === 'partial' && !hasGeneric && boundChannels === 0),
  };
}

export function collectEventWarnings(binding: EventTemplateBindingItem): string[] {
  const warnings: string[] = [];
  const push = (text?: string) => {
    const value = text?.trim();
    if (value && !warnings.includes(value)) warnings.push(value);
  };
  push(binding.generic_binding?.warning);
  for (const slot of binding.channel_bindings || []) {
    push(slot.warning);
  }
  return warnings;
}

export function coverageBadgeClass(kind: BindingCoverageKind): string {
  switch (kind) {
    case 'ok':
      return 'bg-shell-success-soft text-shell-success border-shell-success/25';
    case 'partial':
      return 'bg-shell-primary-soft text-shell-primary border-shell-primary/25';
    case 'legacy':
      return 'bg-shell-warning-soft text-shell-warning border-shell-warning/30';
    case 'gap':
      return 'bg-shell-danger-soft text-shell-danger border-shell-danger/30';
    default:
      return 'bg-shell-hover text-shell-muted border-shell-border';
  }
}

export function slotStatusLabel(slot: EventTemplateBindingSlot, mode: 'generic' | 'override'): string {
  if (slot.template) {
    const active = slot.template.is_active ? 'активен' : 'неактивен';
    return mode === 'generic'
      ? `${slot.template.name} (${active})`
      : `Override: ${slot.template.name} (${active})`;
  }
  if (slotIsLegacy(slot)) return 'Legacy fallback';
  return mode === 'generic' ? 'Не назначен' : 'Override не задан';
}
